/**
 * Recent walkthrough viewers — dashboard widget for owner/admin.
 *
 * Surfaces approved walkthrough viewers ordered by last view, with an
 * "active now" indicator (last viewed in the past 15 minutes). Polls
 * /api/walkthrough/recent-viewers every 60s so prospects you can call
 * while they're reading the doc are visible in real time.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Eye, RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

function rel(ts) {
  if (!ts) return '—';
  try {
    const t = new Date(ts).getTime();
    const dt = Date.now() - t;
    if (dt < 60_000) return 'just now';
    if (dt < 3_600_000) return `${Math.floor(dt / 60_000)}m ago`;
    if (dt < 86_400_000) return `${Math.floor(dt / 3_600_000)}h ago`;
    return new Date(ts).toLocaleDateString();
  } catch { return ts; }
}

export default function RecentWalkthroughViewers({ limit = 12 }) {
  const [data, setData] = useState({ viewers: [], as_of: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/walkthrough/recent-viewers`, { params: { limit } });
      setData(d || { viewers: [], as_of: null });
      setError('');
    } catch (e) {
      // Widget is admin-only; recruiters/candidates get 403 and the parent
      // surface should not have mounted it. Soft-fail with logger.warn so we
      // know in dev.
      logger.warn('recent-viewers failed', e?.response?.status);
      setError(e.response?.data?.detail || 'Could not load recent viewers.');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [load]);

  const liveCount = data.viewers.filter(v => v.active_now).length;

  return (
    <section className="rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] p-5" data-testid="recent-walkthrough-viewers">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="inline-flex items-center gap-2 font-display text-lg text-[var(--vh-text-primary)]">
            <Eye className="w-4 h-4 text-[var(--vh-accent)]" />
            Recent walkthrough viewers
            {liveCount > 0 && (
              <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider border border-emerald-300" data-testid="live-now-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                {liveCount} live now
              </span>
            )}
          </h3>
          <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">Prospects reading your walkthrough or journey docs. Polls every 60 seconds.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--vh-border)] text-xs text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)] disabled:opacity-60"
          data-testid="recent-viewers-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </header>

      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}

      {data.viewers.length === 0 ? (
        <p className="text-sm text-[var(--vh-text-muted)] py-3" data-testid="recent-viewers-empty">
          No approved viewers have opened the walkthrough yet. Once you approve a request from the
          <a href="#" onClick={(e) => { e.preventDefault(); window.location.href = '/admin/dashboard'; }} className="underline ml-1">Walkthrough access</a> tab, opens will appear here.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="recent-viewers-list">
          {data.viewers.map(v => (
            <li
              key={v.id}
              className={`p-3 rounded-xl border ${v.active_now ? 'border-emerald-300 bg-emerald-50/60' : 'border-[var(--vh-border)] bg-[var(--vh-bg-muted)]'}`}
              data-testid={`recent-viewer-${v.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--vh-text-primary)] truncate">
                    {v.full_name}
                    <span className="text-[var(--vh-text-muted)] font-normal"> · {v.organisation}</span>
                  </p>
                  <p className="text-xs text-[var(--vh-text-muted)] mt-0.5 truncate">
                    {v.role} · <a href={`mailto:${v.email}`} className="hover:text-[var(--vh-brand-primary)]">{v.email}</a>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {v.active_now ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold uppercase tracking-wide">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Reading now
                    </span>
                  ) : (
                    <span className="text-[11px] text-[var(--vh-text-muted)]">Last viewed {rel(v.last_viewed_at)}</span>
                  )}
                  <p className="text-[11px] text-[var(--vh-text-muted)] mt-0.5">{v.view_count} view{v.view_count > 1 ? 's' : ''}</p>
                </div>
              </div>
              {v.active_now && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${v.email}?subject=Following up on your Vault Hire review`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-[11px] font-medium"
                    data-testid={`recent-viewer-email-${v.id}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Email now
                  </a>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
